import { Component } from '@theme/component';
import { trackAgeGatePassed, trackAgeGateView } from '@theme/analytics-stubs';
// Imported relatively (not via a @theme/ importmap alias) on purpose: these
// helpers are covered by test/age-gate-utils.test.mjs, which runs in Node where
// importmap aliases don't resolve. Keeping one relative path lets the browser
// and the Node tests share the exact same module.
import { createVerification, validateDateOfBirth } from './age-gate-utils.js';

// Shown duration of the intro screen before the prompt fades in.
const INTRO_DURATION_MS = 1000;

/**
 * @typedef {object} AgeGateRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} intro
 * @property {HTMLElement} prompt
 * @property {HTMLElement} [simplePanel]
 * @property {HTMLElement} [datePanel]
 * @property {HTMLElement} configError
 * @property {HTMLElement} [declineMessage]
 * @property {HTMLElement} [dateError]
 * @property {HTMLInputElement} [month]
 * @property {HTMLInputElement} [day]
 * @property {HTMLInputElement} [year]
 */

/** @extends {Component<AgeGateRefs>} */
class AgeGateComponent extends Component {
  // Only the refs present in every render state are required. The mode panels
  // and date fields may be rendered conditionally; if they were required, a
  // missing panel would throw MissingRefError in connectedCallback and abort
  // the upgrade. Because the render-blocking preflight has already opened the
  // modal on the `intro` screen, aborting here would leave the visitor locked
  // on that screen — the CSS intro floor (age-gate.css) exists to reveal the
  // prompt in exactly that case. Methods using optional refs guard for absence.
  requiredRefs = ['dialog', 'intro', 'prompt', 'configError'];

  #introTimer;
  #declineTimer;
  #revealTimer;
  #focusTimer;
  #isPreview = false;
  #viewTracked = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#preventClose);
    document.addEventListener('shopify:section:select', this.#handleSectionSelect);
    document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);

    if (this.dataset.preflight === 'skip' && !this.designMode) {
      trackAgeGatePassed();
      return;
    }

    if (this.dataset.preflight === 'show') this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('cancel', this.#preventClose);
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
    this.#clearTimers();
  }

  get configuration() {
    return {
      market: this.dataset.market || '',
      minimumAge: Number(this.dataset.minimumAge),
      mode: this.dataset.mode || '',
    };
  }

  get designMode() {
    return this.dataset.designMode === 'true';
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #preventClose = (event) => event.preventDefault();

  #handleSectionSelect = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#isPreview = true;
    this.hidden = false;
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.#start();
  };

  #handleSectionDeselect = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#closeImmediately();
    this.hidden = true;
    this.#isPreview = false;
  };

  #start() {
    this.#clearTimers();
    this.#clearDateError();

    if (this.dataset.configValid !== 'true') {
      this.#setState('config-error');
      this.refs.configError.focus();
      this.#trackView();
      return;
    }

    this.#trackView();
    if (this.reducedMotion) {
      this.#showPrompt();
      return;
    }

    this.#setState('intro');
    this.refs.intro.focus();
    this.#introTimer = window.setTimeout(() => this.#showPrompt(), INTRO_DURATION_MS);
  }

  #showPrompt() {
    this.#setState('prompt');
    const activePanel = this.configuration.mode === 'date' ? this.refs.datePanel : this.refs.simplePanel;
    const initialControl = activePanel?.querySelector('input, button');

    // On short viewports the first control sits below the fold — focus the
    // screen itself instead, so the browser does not scroll the header
    // (logo/wordmark) out of view. Hidden elements still have layout, so the
    // rects are valid even before the fade-in finishes.
    const screen = this.refs.prompt;
    const fitsInView =
      initialControl instanceof HTMLElement &&
      initialControl.getBoundingClientRect().bottom <= screen.getBoundingClientRect().bottom;
    this.#focusWhenVisible(fitsInView ? initialControl : screen);
  }

  // The prompt screen fades in via a visibility transition; focus() fails
  // silently while it is still hidden. Retry briefly until focus sticks.
  #focusWhenVisible(target, attempts = 20) {
    window.clearTimeout(this.#focusTimer);
    if (this.dataset.state !== 'prompt') return;
    target.focus({ preventScroll: true });
    if (document.activeElement === target || attempts <= 0) return;
    this.#focusTimer = window.setTimeout(() => this.#focusWhenVisible(target, attempts - 1), 25);
  }

  #setState(state) {
    this.dataset.state = state;
    this.#syncInertScreens(state);
  }

  // Screens are stacked and only cross-faded via a visibility transition that
  // is deliberately delayed by the fade-out duration (see age-gate.css). During
  // that window an off-screen panel is still `visibility: visible` with
  // `opacity: 0`, so its inputs/links stay in the tab order and are exposed to
  // screen readers while invisible. Marking every non-active screen `inert`
  // removes it from focus and the accessibility tree regardless of where the
  // CSS transition currently sits. The native modal dialog keeps focus inside
  // the gate; `inert` keeps it on the screen the user can actually see.
  #syncInertScreens(state) {
    const activeScreen =
      state === 'intro' ? this.refs.intro : state === 'config-error' ? this.refs.configError : this.refs.prompt;

    for (const screen of [this.refs.intro, this.refs.prompt, this.refs.configError]) {
      if (!screen) continue;
      screen.inert = screen !== activeScreen;
    }
  }

  #trackView() {
    if (this.designMode || this.#viewTracked) return;
    this.#viewTracked = true;
    trackAgeGateView();
  }

  acceptSimple() {
    this.#accept();
  }

  decline() {
    // `decline_redirect` is a merchant-only `url` setting (Theme Editor), not
    // visitor input — sending an under-age visitor off-site (e.g. a corporate
    // or responsibility page) is an intended use. We therefore allow absolute
    // http(s) URLs to any domain, plus same-origin relative paths. The regex
    // only blocks dangerous schemes: `/(?!/)` rejects protocol-relative URLs
    // (`//host`) and the `https?://` anchor rejects `javascript:` etc., so no
    // non-navigational scheme reaches location.replace().
    const redirect = this.dataset.declineRedirect;
    if (redirect && /^(https?:\/\/|\/(?!\/))/.test(redirect) && !this.#isPreview) {
      window.location.replace(redirect);
      return;
    }

    // The decline message is toggled via display, so a live region would not
    // announce it reliably. Moving focus to it announces the message exactly
    // once AND keeps a focus anchor — the prompt controls (including the
    // focused button) are display:none while declined. #showPrompt() returns
    // focus to the first control once the prompt is restored.
    this.#setState('declined');
    this.refs.declineMessage?.focus({ preventScroll: true });
    // The schema clamps decline_delay to 1-30s (default 5); `> 0` only guards
    // against a missing or non-numeric data-attribute.
    const declineDelay = Number(this.dataset.declineDelay);
    const delay = (declineDelay > 0 ? declineDelay : 5) * 1000;
    this.#declineTimer = window.setTimeout(() => this.#resetPrompt(), delay);
  }

  submitDate(event) {
    event.preventDefault();
    const { month, day, year } = this.refs;
    if (!month || !day || !year) return;
    const values = [month.value, day.value, year.value];

    if (values[0].length !== 2 || values[1].length !== 2 || values[2].length !== 4) {
      this.#showDateError(this.dataset.errorIncomplete || '');
      return;
    }

    const result = validateDateOfBirth(
      {
        month: Number(values[0]),
        day: Number(values[1]),
        year: Number(values[2]),
      },
      this.configuration.minimumAge
    );

    if (result === 'invalid') {
      this.#showDateError(this.dataset.errorInvalid || '');
      return;
    }

    if (result === 'underage') {
      this.#clearDateError();
      this.decline();
      return;
    }

    this.#accept();
  }

  handleDateInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    input.value = input.value.replace(/\D/g, '').slice(0, input.maxLength);
    this.#clearDateError();

    if (input.value.length !== input.maxLength) return;

    // Advance to the next not-yet-complete field in the rendered order, so
    // auto-advance follows the visual layout (day-first vs. month-first).
    const fields = this.#orderedDateFields();
    const next = fields.slice(fields.indexOf(input) + 1).find((field) => field.value.length < field.maxLength);
    if (next) next.focus();
  }

  #orderedDateFields() {
    const order = (this.refs.datePanel.dataset.fieldOrder || 'month day year').split(' ');
    return order.map((name) => this.refs[name]).filter((field) => field instanceof HTMLInputElement);
  }

  #accept() {
    if (!this.#isPreview) {
      this.#writeCookie();
      trackAgeGatePassed();
    }

    this.#setState('revealing');
    if (this.reducedMotion) {
      this.#closeImmediately();
      return;
    }

    this.#revealTimer = window.setTimeout(() => this.#closeImmediately(), this.#revealDurationMs());
  }

  // The reveal animation length lives in CSS as --age-gate-reveal-duration
  // (assets/age-gate.css); reading it here keeps the close timer and the
  // animation from drifting apart when the CSS is tuned.
  #revealDurationMs() {
    const raw = getComputedStyle(this).getPropertyValue('--age-gate-reveal-duration').trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 1000;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  #writeCookie() {
    const maxAge = Number(this.dataset.cookieDays) * 86400;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    // Cookie name comes from Liquid (data-cookie-name) so it stays in sync with
    // the preflight script in snippets/age-gate-preflight.liquid.
    document.cookie = `${this.dataset.cookieName}=${createVerification(
      this.configuration
    )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  }

  #dateInputs() {
    return [this.refs.month, this.refs.day, this.refs.year].filter((input) => input instanceof HTMLInputElement);
  }

  #showDateError(message) {
    if (this.refs.dateError) {
      this.refs.dateError.textContent = message;
      this.refs.dateError.hidden = false;
    }
    for (const input of this.#dateInputs()) input.setAttribute('aria-invalid', 'true');
    this.#orderedDateFields()[0]?.focus();
  }

  #clearDateError() {
    if (this.refs.dateError) {
      this.refs.dateError.textContent = '';
      this.refs.dateError.hidden = true;
    }
    for (const input of this.#dateInputs()) input.removeAttribute('aria-invalid');
  }

  #resetPrompt() {
    this.#clearTimers();
    for (const input of this.#dateInputs()) input.value = '';
    this.#clearDateError();
    this.#showPrompt();
  }

  #closeImmediately() {
    this.#clearTimers();
    if (this.refs.dialog.open) this.refs.dialog.close();
    document.documentElement.removeAttribute('scroll-lock');
    this.#setState('closed');
    this.#restoreFocus();
  }

  // Move focus to the main content after the gate closes on the storefront so
  // screen-reader / keyboard users land at the top of the page instead of on
  // <body>. Skipped in the theme editor, where forcing focus disrupts editing.
  #restoreFocus() {
    if (this.#isPreview || this.designMode) return;

    const main = document.getElementById('MainContent');
    if (!main) return;

    // #MainContent has no tabindex (the skip link relies on native fragment
    // focus), so make it focusable just for this move, then clean up on blur.
    const hadTabindex = main.hasAttribute('tabindex');
    if (!hadTabindex) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
    if (!hadTabindex) {
      main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
    }
  }

  #clearTimers() {
    window.clearTimeout(this.#introTimer);
    window.clearTimeout(this.#declineTimer);
    window.clearTimeout(this.#revealTimer);
    window.clearTimeout(this.#focusTimer);
  }
}

if (!customElements.get('age-gate-component')) customElements.define('age-gate-component', AgeGateComponent);
